import { useCallback, useEffect, useRef, useState } from "react";
import { getChangelog, markChangelogSeen, ChangelogEntry } from "../api/changelogAPI";
import { tokenIdentity } from "../api/authTokenStore";

// Give onboarding a beat to claim the screen before we consider auto-opening.
const AUTO_OPEN_DELAY_MS = 1500;

function onboardingActive(): boolean {
  if (typeof document === "undefined") return false;
  return !!document.querySelector('[data-onboarding-overlay="true"]');
}

type ChangelogSnapshot = {
  entries: ChangelogEntry[];
  total: number;
  unread: boolean;
  latestId: string | null;
};

/**
 * Cached process-wide so a remount costs nothing: on 2026-09-14 a session
 * remounting SideNavbarLayout ~1.3x a second turned this one "fetch once" into
 * 121 requests every 90 seconds. See the note in useUserPreferences.
 *
 * Since What's New moved onto @mediatechgroup/changelog (PM 86b47f32) the
 * answer is per reader (audiences, and the reader's own read state), so the
 * cache is keyed on who the token is for and which club is open, not shared
 * by everyone. A "seen" stamp updates the cached answer, so a remount never
 * reopens what was just dismissed.
 */
let changelogCache: { key: string; snap: ChangelogSnapshot } | null = null;
let changelogInflight: { key: string; promise: Promise<ChangelogSnapshot | null> } | null = null;

/** Test seam: forget the cached changelog. */
export function _resetChangelogCache(): void {
  changelogCache = null;
  changelogInflight = null;
}

function cacheKey(token: string): string {
  const club = typeof window !== "undefined" ? window.location.pathname.split("/")[1] || "" : "";
  return `${tokenIdentity(token) || token}|${club}`;
}

/**
 * Coordinates the "What's New" changelog modal for staff/admin:
 *  - fetches this reader's What's New (page 0) with their unread state,
 *  - auto-opens ONCE per session when there's unseen content AND no
 *    onboarding walkthrough is on screen (onboarding wins),
 *  - stamps seen the moment it auto-opens (so "shown once → don't
 *    auto-show again until something newer is published," regardless of how
 *    the user dismisses it — even closing the tab without clicking ✕),
 *  - exposes open/close; close stamps too when something was still unseen.
 *
 * Seen state is the reader's changelog_reads row (POST /changelog/seen), no
 * longer the last_seen_changelog_at preference.
 *
 * Render the modal yourself from `entries`/`total` when `open` is true.
 */
export function useWhatsNew(enabled: boolean) {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [unseen, setUnseen] = useState(false);
  const [latestId, setLatestId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const autoOpenedRef = useRef(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;

  // Fetch the changelog once per reader per process, not once per mount.
  useEffect(() => {
    if (!enabled || !token) return;
    let cancelled = false;
    const key = cacheKey(token);

    const apply = (snap: ChangelogSnapshot) => {
      setEntries(snap.entries);
      setTotal(snap.total);
      setUnseen(snap.unread);
      setLatestId(snap.latestId);
      setLoaded(true);
    };

    if (changelogCache && changelogCache.key === key) {
      apply(changelogCache.snap);
      return;
    }

    if (!changelogInflight || changelogInflight.key !== key) {
      const promise: Promise<ChangelogSnapshot | null> = getChangelog(token, { limit: 50 })
        .then((res) => {
          const snap: ChangelogSnapshot = {
            entries: res?.data || [],
            total: res?.total || 0,
            unread: !!res?.unread,
            latestId: res?.latest_id || null,
          };
          changelogCache = { key, snap };
          return snap;
        })
        .catch(() => null)
        .finally(() => {
          if (changelogInflight?.promise === promise) changelogInflight = null;
        });
      changelogInflight = { key, promise };
    }

    changelogInflight.promise.then((snap) => {
      if (cancelled) return;
      if (snap) apply(snap);
      else setLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, token]);

  const markSeen = useCallback(() => {
    if (!unseen || !latestId) return;
    setUnseen(false);
    if (token) {
      const key = cacheKey(token);
      if (changelogCache && changelogCache.key === key) {
        changelogCache = { key, snap: { ...changelogCache.snap, unread: false } };
      }
    }
    void markChangelogSeen(token, latestId);
  }, [unseen, latestId, token]);

  // Auto-open (once) when there's unseen content and onboarding isn't up.
  useEffect(() => {
    if (!enabled || !loaded) return;
    if (autoOpenedRef.current) return;
    if (!unseen || entries.length === 0) return;

    const t = setTimeout(() => {
      if (autoOpenedRef.current) return;
      if (onboardingActive()) return; // defer — onboarding wins this mount
      autoOpenedRef.current = true;
      setOpen(true);
      // Stamp seen as soon as we SHOW it — so it won't auto-open again
      // until a newer entry is released, even if the user never explicitly
      // closes it (navigates away / closes the tab).
      markSeen();
    }, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [enabled, loaded, unseen, entries.length, markSeen]);

  const openModal = useCallback(() => setOpen(true), []);

  const close = useCallback(() => {
    setOpen(false);
    markSeen();
  }, [markSeen]);

  return { entries, total, open, openModal, close, unseen, loaded };
}
